import { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { CircularProgress, Box } from '@mui/material';
import { useCryptoKeyStore, KeyOwnerType } from '../../features/encryption';
import { apiClient } from '../../api/client';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRoles?: KeyOwnerType[];
}

export function ProtectedRoute({ children, requiredRoles }: ProtectedRouteProps) {
  const { activeKey, isInitialized, initialize, gkdkSlots, loadGKDKSlots } = useCryptoKeyStore();
  const location = useLocation();

  // Initialize key store on mount
  useEffect(() => {
    if (!isInitialized) {
      initialize();
    }
  }, [isInitialized, initialize]);

  // Sync keyId to API client for X-Key-Id header
  useEffect(() => {
    if (activeKey) {
      apiClient.setKeyId(activeKey.keyId);
    } else {
      apiClient.clearKeyId();
    }
  }, [activeKey]);

  // Load GKDK slots after initialization to get authority access
  useEffect(() => {
    if (isInitialized && activeKey && gkdkSlots.length === 0) {
      loadGKDKSlots();
    }
  }, [isInitialized, activeKey, gkdkSlots.length, loadGKDKSlots]);

  // Show loading while initializing
  if (!isInitialized) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Redirect to login if no active key
  if (!activeKey) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check role requirements
  if (requiredRoles && requiredRoles.length > 0 && activeKey.ownerType) {
    const hasRequiredRole = requiredRoles.includes(activeKey.ownerType);
    if (!hasRequiredRole) {
      return <Navigate to="/" replace />;
    }
  }

  return <>{children}</>;
}
